import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Ticket, Printer, CheckCircle2, Circle, Loader2, Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EventTicketsDialog = ({ event, open, onClose }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | used | pending

  const fetchCodes = async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/discounts/${event.id}/codes`);
      setCodes(r.data || []);
    } catch (err) {
      toast.error('No se pudieron cargar los tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && event?.id) fetchCodes();
    if (!open) { setSearch(''); setFilter('all'); }
  }, [open, event?.id]);

  const toggleUsed = async (code) => {
    setTogglingId(code.id);
    try {
      const r = await axios.put(`${API_URL}/api/discounts/${code.id}/toggle-used`);
      setCodes((prev) => prev.map((c) => (c.id === code.id ? { ...c, used_at: r.data.used_at } : c)));
    } catch (err) {
      toast.error('Error al actualizar el ticket');
    } finally {
      setTogglingId(null);
    }
  };

  const usedCount = codes.filter(c => c.used_at).length;
  const pendingCount = codes.length - usedCount;

  const filtered = codes.filter((c) => {
    if (filter === 'used' && !c.used_at) return false;
    if (filter === 'pending' && c.used_at) return false;
    if (search && !c.code.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const printList = () => {
    // Open a new window with a printable view
    const url = `/provider/tickets-print/${event.id}`;
    window.open(url, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col" data-testid="tickets-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#00CC66] font-bold mb-1">
                <Ticket className="w-3.5 h-3.5" /> Inventario de tickets
              </div>
              <div className="text-xl font-black text-[#064E3B] truncate">{event?.name}</div>
            </div>
            <Button onClick={printList} className="bg-[#064E3B] hover:bg-[#053d2f] text-white rounded-full h-10 px-4 flex-shrink-0" data-testid="tickets-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          </DialogTitle>
        </DialogHeader>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 px-6 py-4 bg-gray-50 border-b border-gray-100">
          <button onClick={() => setFilter('all')}
                  className={`text-left rounded-2xl p-3 transition-colors ${filter === 'all' ? 'bg-white ring-2 ring-[#00CC66]' : 'bg-white hover:ring-1 hover:ring-gray-200'}`}
                  data-testid="tickets-filter-all">
            <div className="text-3xl font-black text-[#064E3B] leading-none">{codes.length}</div>
            <div className="text-xs text-gray-500 mt-1 font-semibold">Total generados</div>
          </button>
          <button onClick={() => setFilter('pending')}
                  className={`text-left rounded-2xl p-3 transition-colors ${filter === 'pending' ? 'bg-orange-50 ring-2 ring-orange-400' : 'bg-white hover:ring-1 hover:ring-gray-200'}`}
                  data-testid="tickets-filter-pending">
            <div className="text-3xl font-black text-orange-500 leading-none">{pendingCount}</div>
            <div className="text-xs text-gray-500 mt-1 font-semibold">Por usar</div>
          </button>
          <button onClick={() => setFilter('used')}
                  className={`text-left rounded-2xl p-3 transition-colors ${filter === 'used' ? 'bg-[#00CC66]/10 ring-2 ring-[#00CC66]' : 'bg-white hover:ring-1 hover:ring-gray-200'}`}
                  data-testid="tickets-filter-used">
            <div className="text-3xl font-black text-[#00CC66] leading-none">{usedCount}</div>
            <div className="text-xs text-gray-500 mt-1 font-semibold">Ya usados</div>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4 pb-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código..."
              className="pl-10 pr-10 rounded-full h-10"
              data-testid="tickets-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Ticket className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                {codes.length === 0 ? 'Aún no hay tickets generados para este evento' : 'No hay resultados con ese filtro'}
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="tickets-list">
              {filtered.map((c) => {
                const isUsed = !!c.used_at;
                const createdDate = c.created_at ? new Date(c.created_at) : null;
                return (
                  <div key={c.id}
                       className={`flex items-center gap-3 p-3 rounded-2xl border transition-colors ${
                         isUsed ? 'bg-[#00CC66]/5 border-[#00CC66]/30' : 'bg-white border-gray-200 hover:border-[#00CC66]/40'
                       }`}
                       data-testid={`ticket-${c.code}`}>
                    <button
                      onClick={() => toggleUsed(c)}
                      disabled={togglingId === c.id}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        isUsed ? 'bg-[#00CC66] text-white hover:bg-[#00b359]' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      title={isUsed ? 'Marcar como no usado' : 'Marcar como usado'}
                      data-testid={`ticket-toggle-${c.code}`}
                    >
                      {togglingId === c.id ? <Loader2 className="w-5 h-5 animate-spin" /> :
                       isUsed ? <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} /> :
                       <Circle className="w-5 h-5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`font-mono font-black text-lg tracking-wider ${isUsed ? 'text-[#00CC66] line-through opacity-70' : 'text-[#064E3B]'}`}>
                        {c.code}
                      </div>
                      <div className="text-xs text-gray-500">
                        Generado {createdDate ? createdDate.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </div>
                    </div>
                    {isUsed && (
                      <Badge className="bg-[#00CC66] text-white border-0 rounded-full text-xs flex-shrink-0">
                        Usado
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-500 text-center bg-gray-50">
          💡 Marca los tickets a medida que llegan los asistentes. El check queda guardado.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventTicketsDialog;
